import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { SubscriptionFeature, SubscriptionPlan, formatPrice } from '../../types/subscription';
import { useSubscription } from '../../contexts/SubscriptionContext';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const Container = styled.div`
  background: linear-gradient(135deg, rgba(13, 26, 38, 0.95) 0%, rgba(20, 40, 60, 0.95) 100%);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 16px;
  padding: 32px;
  text-align: center;
  max-width: 500px;
  margin: 40px auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
`;

const IconWrapper = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(0, 212, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 28px;
`;

const Title = styled.h2`
  color: #fff;
  font-size: 1.5rem;
  margin: 0 0 12px 0;
  font-weight: 600;
`;

const Description = styled.p`
  color: #b0b0b0;
  font-size: 1rem;
  line-height: 1.6;
  margin: 0 0 24px 0;
`;

const PlanSelector = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  justify-content: center;
`;

const BillingToggle = styled.button<{ $active: boolean }>`
  padding: 10px 20px;
  border-radius: 8px;
  border: 1px solid ${props => props.$active ? '#00d4ff' : 'rgba(255, 255, 255, 0.2)'};
  background: ${props => props.$active ? 'rgba(0, 212, 255, 0.15)' : 'transparent'};
  color: ${props => props.$active ? '#00d4ff' : '#999'};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #00d4ff;
  }
`;

const PriceDisplay = styled.div`
  margin-bottom: 24px;
`;

const Price = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #00d4ff;
  margin-bottom: 4px;
`;

const PricePeriod = styled.div`
  color: #666;
  font-size: 0.9rem;
`;

const Savings = styled.div`
  display: inline-block;
  background: rgba(76, 175, 80, 0.2);
  color: #4caf50;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  margin-top: 8px;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 24px 0;
  text-align: left;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  color: #e0e0e0;
  font-size: 0.95rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
`;

const CheckIcon = styled.span`
  color: #4caf50;
  font-size: 1rem;
`;

const UpgradeButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #00d4ff 0%, #0099cc 100%);
  border: none;
  border-radius: 10px;
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 212, 255, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const TrialNote = styled.div`
  margin-top: 16px;
  color: #666;
  font-size: 0.85rem;
`;

// Маппинг фич на читаемые названия
const featureNames: Record<SubscriptionFeature, string> = {
  analytics: 'Analytics Dashboard',
  management: 'Management Dashboard',
  export_data: 'Data Export',
  advanced_reports: 'Advanced Reports',
  priority_support: 'Priority Support',
  custom_branding: 'Custom Branding',
  api_access: 'API Access',
  unlimited_maps: 'Unlimited Maps',
  unlimited_referees: 'Unlimited Referees',
};

interface UpgradePromptProps {
  feature?: SubscriptionFeature;
  featureName?: string;
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({ feature, featureName }) => {
  const { plans, loadPlans, redirectToCheckout, isTrialing, trialDaysRemaining } = useSubscription();
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!plans) {
      loadPlans();
    }
  }, [plans, loadPlans]);

  const proPlan = plans?.find(p => p.name === 'pro');
  const displayFeatureName = featureName || (feature ? featureNames[feature] : 'Premium Features');

  const handleUpgrade = async () => {
    if (!proPlan) return;
    setLoading(true);
    try {
      await redirectToCheckout(proPlan.id, billingInterval);
    } catch (err) {
      console.error('Error redirecting to checkout:', err);
    } finally {
      setLoading(false);
    }
  };

  const monthlyPrice = proPlan?.priceMonthly || 2999;
  const yearlyPrice = proPlan?.priceYearly || 29990;
  const yearlyMonthly = Math.round(yearlyPrice / 12);
  const savings = Math.round(((monthlyPrice * 12 - yearlyPrice) / (monthlyPrice * 12)) * 100);

  return (
    <Container>
      <IconWrapper>
        <span role="img" aria-label="upgrade">&#x1F512;</span>
      </IconWrapper>

      <Title>Upgrade to Pro</Title>
      <Description>
        {feature
          ? `${displayFeatureName} is a Pro feature. Upgrade your subscription to unlock full access.`
          : 'Unlock all premium features with a Pro subscription.'}
      </Description>

      <PlanSelector>
        <BillingToggle
          $active={billingInterval === 'monthly'}
          onClick={() => setBillingInterval('monthly')}
        >
          Monthly
        </BillingToggle>
        <BillingToggle
          $active={billingInterval === 'yearly'}
          onClick={() => setBillingInterval('yearly')}
        >
          Yearly
        </BillingToggle>
      </PlanSelector>

      <PriceDisplay>
        <Price>
          {billingInterval === 'monthly'
            ? formatPrice(monthlyPrice)
            : formatPrice(yearlyMonthly)
          }
          <span style={{ fontSize: '1rem', fontWeight: 400 }}>/month</span>
        </Price>
        {billingInterval === 'yearly' && (
          <>
            <PricePeriod>Billed annually ({formatPrice(yearlyPrice)}/year)</PricePeriod>
            <Savings>Save {savings}%</Savings>
          </>
        )}
      </PriceDisplay>

      <FeaturesList>
        <FeatureItem>
          <CheckIcon>&#10003;</CheckIcon>
          Advanced Analytics Dashboard
        </FeatureItem>
        <FeatureItem>
          <CheckIcon>&#10003;</CheckIcon>
          Export Data to CSV/Excel
        </FeatureItem>
        <FeatureItem>
          <CheckIcon>&#10003;</CheckIcon>
          Unlimited Maps
        </FeatureItem>
        <FeatureItem>
          <CheckIcon>&#10003;</CheckIcon>
          Unlimited Referees
        </FeatureItem>
        <FeatureItem>
          <CheckIcon>&#10003;</CheckIcon>
          Priority Support
        </FeatureItem>
      </FeaturesList>

      <UpgradeButton onClick={handleUpgrade} disabled={loading || !proPlan}>
        {loading ? 'Loading...' : 'Upgrade Now'}
      </UpgradeButton>

      {isTrialing && trialDaysRemaining !== null && trialDaysRemaining > 0 && (
        <TrialNote>
          Your trial ends in {trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''}
        </TrialNote>
      )}
    </Container>
  );
};

export default UpgradePrompt;
